import React, { useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { isNull, isUndefined } from 'lodash';

import { alturaVolume, t } from '~/helpers';
import { Spinner } from '~/components';
import { Input, Autocomplete, Select, Hidden } from '~/components/Form';
import {
  storeArqueacao,
  updateArqueacao,
} from '~/store/modules/arqueacao/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.arqueacao);

  const initials = create ? data : { ...find, ...data };

  const optionMedida = useMemo(
    () => [
      { value: 'CM', label: 'CM' },
      { value: 'MM', label: 'MM' },
    ],
    []
  );

  const handleSetValue = useCallback((field, value) => {
    reference.current.setFieldValue(field, value);
  }, []);

  async function handleSubmit(param) {
    try {
      const { itens, arqu_altura_maxima, arqu_volume_maximo } = param;

      if (itens) {
        const message = alturaVolume(
          itens,
          arqu_altura_maxima,
          arqu_volume_maximo
        );

        if (!isUndefined(message)) {
          reference.current.setFieldError('itens', t(message));
          throw Error(message);
        }
      }

      const schema = Yup.object().shape({
        poco_codigo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        arqu_unidade_medida: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        arqu_descricao: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30)),
        arqu_altura_maxima: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
          .max(999, t('s.geral.validacao.tamanhomaximo').replace('%s', 999)),
        arqu_volume_maximo: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .min(1, t('s.geral.validacao.tamanhominimo').replace('%s', 1))
          .max(
            999999999,
            t('s.geral.validacao.tamanhomaximo').replace('%s', 999999999)
          ),
        itens: Yup.string().required(t('s.geral.validacao.campoobrigatorio')),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        arqu_altura_maxima: Number(param.arqu_altura_maxima),
        arqu_volume_maximo: Number(param.arqu_volume_maximo),
        idcliente: client,
      };

      if (create) {
        await dispatch(storeArqueacao(objSend, params));
      } else {
        await dispatch(updateArqueacao(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Autocomplete
            name="poco_codigo"
            label="poco_descricao"
            title="Posto"
            url="postoscomboios"
            unique="poco_codigo"
            disabled={!create}
            getValue={({ poco_descricao }) => {
              if (!isNull(poco_descricao)) {
                handleSetValue('poco_descricao', poco_descricao);
              }
            }}
          />

          <Hidden name="poco_descricao" />

          <Select
            name="arqu_unidade_medida"
            label="Unid. Med."
            options={optionMedida}
          />

          <Input name="arqu_descricao" label="Descrição" />

          <Input
            name="arqu_altura_maxima"
            label="Altura Máxima"
            type="number"
          />

          <Input
            name="arqu_volume_maximo"
            label="Volume Máximo"
            type="number"
          />

          <Input name="itens" label="Itens" multiline rows="5" rowsMax="5" />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
